import React, { useEffect, useMemo, useState } from "react";
import { Autocomplete, TextField } from "@mui/material";
import SyncRoundedIcon from "@mui/icons-material/SyncRounded";
import CloudOffRoundedIcon from "@mui/icons-material/CloudOffRounded";
import { checkDirectory, applyDirectory, saveDirectorySettings } from "./accessApi";
import { Notice, RoleChip, formatWhen, timeAgo } from "./accessUi";

const STATUS = {
  applied: { label: "Applied", tone: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300" },
  preview: { label: "Preview", tone: "bg-sky-500/15 text-sky-700 dark:text-sky-300" },
  held: { label: "Waiting for review", tone: "bg-amber-500/15 text-amber-800 dark:text-amber-200" },
  aborted: { label: "Stopped", tone: "bg-rose-500/15 text-rose-700 dark:text-rose-300" },
  failed: { label: "Failed", tone: "bg-rose-500/15 text-rose-700 dark:text-rose-300" },
};

const StatusBadge = ({ status }) => {
  const s = STATUS[status] || STATUS.preview;
  return <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${s.tone}`}>{s.label}</span>;
};

const Toggle = ({ checked, onChange, disabled, label, hint }) => (
  <label className="flex cursor-pointer items-start gap-3 py-1.5">
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      disabled={disabled}
      className="mt-0.5 h-4 w-4 shrink-0 accent-sky-600"
    />
    <span>
      <span className="text-sm font-medium">{label}</span>
      {hint && <span className="app-muted block text-xs">{hint}</span>}
    </span>
  </label>
);

// The people a preview/held run found, with checkboxes to apply some or all.
const ReviewList = ({ title, rows, picked, setPicked, disabled, showReasons }) => {
  if (!rows.length) return null;
  const all = rows.every((r) => picked.has(r.id));
  return (
    <div>
      <div className="mb-1 flex items-center justify-between gap-2">
        <p className="app-eyebrow">
          {title} ({rows.length})
        </p>
        <button
          type="button"
          onClick={() => setPicked(all ? new Set() : new Set(rows.map((r) => r.id)))}
          className="app-link text-xs"
        >
          {all ? "Select none" : "Select all"}
        </button>
      </div>
      <ul className="app-surface max-h-72 divide-y divide-[color:var(--app-divider)] overflow-y-auto rounded-xl border border-[color:var(--app-surface-border)]">
        {rows.map((row) => (
          <li key={row.id}>
            <label className="flex cursor-pointer items-start gap-3 px-3 py-2">
              <input
                type="checkbox"
                checked={picked.has(row.id)}
                disabled={disabled}
                onChange={() =>
                  setPicked((prev) => {
                    const next = new Set(prev);
                    next.has(row.id) ? next.delete(row.id) : next.add(row.id);
                    return next;
                  })
                }
                className="mt-0.5 h-4 w-4 shrink-0 accent-sky-600"
              />
              <span className="min-w-0">
                <span className="block truncate text-sm font-medium">{row.name}</span>
                <span className="app-muted block truncate text-xs">
                  {showReasons ? row.reasons?.join(" · ") : "Back in Microsoft 365"}
                  {row.email ? ` · ${row.email}` : ""}
                </span>
              </span>
            </label>
          </li>
        ))}
      </ul>
    </div>
  );
};

// Microsoft 365 directory sync: settings, the latest run, and its review list.
const DirectorySyncCard = ({ directory, users, actorId, onChanged }) => {
  const { settings, runs, graphConfigured, everyHours } = directory;
  const latest = runs[0] || null;
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [confirmAuto, setConfirmAuto] = useState(false);
  const [protectInput, setProtectInput] = useState("");
  const [pickedOff, setPickedOff] = useState(() => new Set());
  const [pickedOn, setPickedOn] = useState(() => new Set());

  // Whatever the latest run is still waiting on, pre-ticked.
  const pending = latest && ["preview", "held"].includes(latest.status);
  useEffect(() => {
    setPickedOff(new Set(pending ? (latest.deactivate || []).map((r) => r.id) : []));
    setPickedOn(new Set(pending ? (latest.reactivate || []).map((r) => r.id) : []));
  }, [latest, pending]);

  const usersById = useMemo(() => new Map(users.filter((u) => u.id).map((u) => [u.id, u])), [users]);
  const protectedPeople = settings.protected.map((id) => usersById.get(id) || { id, name: "Unknown user" });
  const protectOptions = useMemo(
    () => users.filter((u) => u.id && !settings.protected.includes(u.id)),
    [users, settings.protected]
  );

  const run = async (fn, success) => {
    setBusy(true);
    setMessage(null);
    try {
      const text = await fn();
      setMessage({ type: "success", text: text || success });
      await onChanged();
    } catch (err) {
      setMessage({ type: "error", text: err.message || "Couldn't do that." });
    } finally {
      setBusy(false);
    }
  };

  const save = (patch, success) => run(() => saveDirectorySettings(actorId, patch), success);

  const checkNow = () =>
    run(async () => {
      const { run: result } = await checkDirectory(actorId);
      if (result.status === "aborted" || result.status === "failed") return result.error;
      const off = result.deactivate?.length || 0;
      const on = result.reactivate?.length || 0;
      return off || on
        ? `Checked. ${off} to deactivate, ${on} to reactivate — review them below.`
        : "Checked. Everyone in Node is still in Microsoft 365.";
    });

  const apply = () =>
    run(async () => {
      const { run: result } = await applyDirectory(actorId, {
        deactivate: [...pickedOff],
        reactivate: [...pickedOn],
      });
      if (result.status !== "applied") return result.error || "Nothing was changed.";
      const off = result.applied?.deactivated?.length || 0;
      const on = result.applied?.reactivated?.length || 0;
      return `Deactivated ${off}, reactivated ${on}. Their open Node tabs follow within about 20 seconds.`;
    });

  const auto = settings.mode === "auto";

  return (
    <section className="app-card rounded-2xl border p-4 sm:p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <span className="app-icon-tile h-10 w-10 shrink-0 rounded-xl bg-sky-500/15 text-sky-600 dark:text-sky-300">
            <SyncRoundedIcon fontSize="small" />
          </span>
          <div className="min-w-0">
            <h2 className="font-semibold">Deactivate people who leave</h2>
            <p className="app-muted text-sm">
              Every {everyHours} hours Node compares its accounts with Microsoft 365 and turns off anyone whose
              account is gone.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={checkNow}
          disabled={busy || !graphConfigured}
          className="app-btn app-btn-secondary"
        >
          <SyncRoundedIcon sx={{ fontSize: 18 }} />
          Check now
        </button>
      </div>

      {!graphConfigured && (
        <p className="mt-4 flex gap-2 rounded-xl bg-amber-500/15 px-3 py-2 text-sm text-amber-800 dark:text-amber-200">
          <CloudOffRoundedIcon sx={{ fontSize: 18 }} className="mt-0.5 shrink-0" />
          Microsoft 365 isn't connected on this server (GRAPH_CLIENT_ID and the Graph certificate). The sync
          won't run until it is.
        </p>
      )}

      <Notice message={message} className="mt-4" />

      <div className="mt-5 grid gap-6 lg:grid-cols-2">
        {/* Settings */}
        <div className="space-y-5">
          <div>
            <p className="app-eyebrow mb-2">Mode</p>
            <div className="app-surface inline-flex rounded-full border border-[color:var(--app-surface-border)] p-1">
              {[
                { value: "preview", label: "Preview only" },
                { value: "auto", label: "Automatic" },
              ].map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  disabled={busy}
                  aria-pressed={settings.mode === opt.value}
                  onClick={() => {
                    if (opt.value === settings.mode) return;
                    if (opt.value === "auto") setConfirmAuto(true);
                    else save({ mode: "preview" }, "Preview only: the sync records what it would do and changes nothing.");
                  }}
                  className={`rounded-full px-3.5 py-1.5 text-sm font-medium transition ${
                    settings.mode === opt.value
                      ? "bg-sky-600 text-white shadow-sm"
                      : "hover:bg-[color:var(--app-hover)]"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <p className="app-muted mt-2 text-xs">
              {auto
                ? "Accounts are deactivated as soon as a sync finds them. A run that would deactivate an unusually large number of people waits for your review instead."
                : "The sync shows who it would deactivate and changes nothing. Review the list, apply it yourself, or switch to Automatic."}
            </p>
            {confirmAuto && (
              <div className="mt-3 rounded-xl bg-amber-500/15 p-3 text-sm text-amber-900 dark:text-amber-100">
                <p>
                  Turn on automatic deactivation? From the next sync, people the sync finds are signed out of
                  Node without anyone reviewing them first. Use “Check now” to see who that would be today.
                </p>
                <div className="mt-2 flex gap-2">
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => {
                      setConfirmAuto(false);
                      save({ mode: "auto" }, "Automatic deactivation is on.");
                    }}
                    className="app-btn app-btn-sm app-btn-primary"
                  >
                    Turn on
                  </button>
                  <button type="button" onClick={() => setConfirmAuto(false)} className="app-btn app-btn-sm app-btn-ghost">
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>

          <div>
            <p className="app-eyebrow mb-1">Deactivate when</p>
            <Toggle checked disabled label="Their account is deleted or disabled in Microsoft 365" hint="Always on." />
            <Toggle
              checked={settings.deactivateNoMailbox}
              disabled={busy}
              onChange={(v) => save({ deactivateNoMailbox: v }, "Saved.")}
              label="Their mailbox is removed"
              hint="Only people Node has seen with a mailbox — shared logins that never had one aren't affected."
            />
            <Toggle
              checked={settings.deactivateHidden}
              disabled={busy}
              onChange={(v) => save({ deactivateHidden: v }, "Saved.")}
              label="They're hidden from the address book"
              hint="Only when it changes — people already hidden the first time Node checked aren't affected."
            />
          </div>

          <div>
            <p className="app-eyebrow mb-1">Coming back</p>
            <Toggle
              checked={settings.reactivate}
              disabled={busy}
              onChange={(v) => save({ reactivate: v }, "Saved.")}
              label="Reactivate people whose account is restored"
              hint="Only accounts the sync turned off. Anyone deactivated by an admin stays off."
            />
          </div>

          <div>
            <p className="app-eyebrow mb-2">Never deactivate automatically</p>
            <div className="mb-2 flex flex-wrap gap-1.5">
              {protectedPeople.length ? (
                protectedPeople.map((p) => (
                  <RoleChip
                    key={p.id}
                    role={p.name}
                    tone="base"
                    disabled={busy}
                    onRemove={() =>
                      save(
                        { protected: settings.protected.filter((id) => id !== p.id) },
                        `${p.name} can be deactivated by the sync again.`
                      )
                    }
                  />
                ))
              ) : (
                <p className="app-muted text-sm">Nobody — the sync can deactivate anyone.</p>
              )}
            </div>
            <Autocomplete
              size="small"
              options={protectOptions}
              value={null}
              inputValue={protectInput}
              onInputChange={(e, v) => setProtectInput(v)}
              onChange={(e, person) => {
                if (!person) return;
                setProtectInput("");
                save(
                  { protected: [...settings.protected, person.id] },
                  `${person.name} will never be deactivated by the sync.`
                );
              }}
              getOptionLabel={(u) => u.name || u.email || ""}
              filterOptions={(options, { inputValue }) => {
                const q = inputValue.trim().toLowerCase();
                return (q
                  ? options.filter((u) => [u.name, u.email].filter(Boolean).some((f) => f.toLowerCase().includes(q)))
                  : options
                ).slice(0, 50);
              }}
              disabled={busy}
              blurOnSelect
              renderInput={(params) => <TextField {...params} placeholder="Add a person (service or shared logins)…" />}
            />
          </div>
        </div>

        {/* Latest run */}
        <div className="space-y-4">
          <div>
            <p className="app-eyebrow mb-2">Latest check</p>
            {latest ? (
              <div className="app-surface rounded-xl border border-[color:var(--app-surface-border)] p-3 text-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <StatusBadge status={latest.status} />
                  <span className="app-muted text-xs">
                    {timeAgo(latest.at)} · {latest.trigger === "schedule" ? "scheduled" : `by ${latest.by?.name || "an admin"}`}
                  </span>
                </div>
                {latest.counts && (
                  <p className="app-muted mt-2 text-xs">
                    Microsoft 365: {latest.counts.graphUsers} accounts ({latest.counts.graphWithMailbox} active with a
                    mailbox) · Node: {latest.counts.nodeActive} active
                  </p>
                )}
                {latest.error && <p className="mt-2 text-rose-700 dark:text-rose-300">{latest.error}</p>}
                {latest.status === "applied" && (
                  <p className="mt-2">
                    Deactivated {latest.applied?.deactivated?.length || 0}, reactivated{" "}
                    {latest.applied?.reactivated?.length || 0}.
                    {latest.applied?.deactivated?.length > 0 && (
                      <span className="app-muted block text-xs">
                        {latest.applied.deactivated.map((r) => r.name).join(", ")}
                      </span>
                    )}
                  </p>
                )}
                {pending && !(latest.deactivate?.length || latest.reactivate?.length) && (
                  <p className="mt-2">Nothing to change — everyone in Node is still in Microsoft 365.</p>
                )}
              </div>
            ) : (
              <p className="app-muted text-sm">
                {graphConfigured ? "Not run yet. “Check now” runs one straight away." : "Not run yet."}
              </p>
            )}
          </div>

          {pending && (latest.deactivate?.length > 0 || latest.reactivate?.length > 0) && (
            <div className="space-y-3">
              {latest.status === "held" && (
                <Notice
                  message={{
                    type: "info",
                    text: "This scheduled run found more people than usual, so it waited. Check the list, then apply.",
                  }}
                />
              )}
              <ReviewList
                title="Deactivate"
                rows={latest.deactivate || []}
                picked={pickedOff}
                setPicked={setPickedOff}
                disabled={busy}
                showReasons
              />
              <ReviewList
                title="Reactivate"
                rows={latest.reactivate || []}
                picked={pickedOn}
                setPicked={setPickedOn}
                disabled={busy}
              />
              <button
                type="button"
                onClick={apply}
                disabled={busy || (pickedOff.size === 0 && pickedOn.size === 0)}
                className="app-btn app-btn-primary"
              >
                Apply to {pickedOff.size + pickedOn.size} selected
              </button>
              <p className="app-muted text-xs">Each person is checked against Microsoft 365 again before anything changes.</p>
            </div>
          )}

          {runs.length > 1 && (
            <details>
              <summary className="app-link cursor-pointer text-xs">Earlier checks</summary>
              <ul className="mt-2 space-y-1.5">
                {runs.slice(1).map((r) => (
                  <li key={r._id} className="flex flex-wrap items-center gap-2 text-xs">
                    <StatusBadge status={r.status} />
                    <span className="app-muted">{formatWhen(r.at)}</span>
                    <span>
                      {r.status === "applied"
                        ? `−${r.applied?.deactivated?.length || 0} / +${r.applied?.reactivated?.length || 0}`
                        : r.error || `${r.deactivate?.length || 0} found`}
                    </span>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      </div>
    </section>
  );
};

export default DirectorySyncCard;
